// ============================================================
// lib/hooks.js — small, general-purpose React hooks used all over the app.
// None of them knows about designs or accounts, so any screen can use them.
// (Hooks that read the workspace, like useBrandLogo or usePublish, live with
// that state in context/AppContext.js.)
//
// In this file:
//   useFilePicker  a hidden file input, with its "busy" state and error
//   useSearch      a search box: what's typed, and a matcher for it
//   useKeyDown     a keyboard shortcut listener (not while typing in a field)
//   useSavedState  state that's saved in this browser as it changes
//   useInView      has this element come near the screen yet? (draw it then)
//   useOnce        run something once when a screen opens (even in StrictMode)
//   useAsync       a value that loads (a saved video, a state file), kept current
// ============================================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { readSaved, writeSaved } from "./browser";

/**
 * A hidden file input you open with a button.
 *
 *   const photo = useFilePicker({ accept: "image/*", onFiles: async (file) => … });
 *   <button onClick={photo.pick} disabled={photo.busy}>Upload</button>
 *   <input {...photo.inputProps} />
 *   {photo.error && <p role="alert">{photo.error}</p>}
 *
 * onFiles gets one File (or a list with multiple). If it throws, its
 * message is shown as the error. The same file can be picked twice in a row.
 */
export function useFilePicker({ accept, multiple = false, onFiles }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const onChange = async (event) => {
    const files = [...(event.target.files || [])];
    event.target.value = "";
    if (!files.length) return;
    setBusy(true);
    setError("");
    try {
      await onFiles(multiple ? files : files[0]);
    } catch (err) {
      setError((err && err.message) || "That file couldn't be used.");
    }
    setBusy(false);
  };
  return {
    pick: () => ref.current && ref.current.click(),
    busy, error, setError,
    inputProps: { ref, type: "file", accept, multiple, hidden: true, onChange },
  };
}

/** Words for searching: lower case, accents removed ("Café" → "cafe"). */
export const searchWords = (text) => String(text || "").toLowerCase().normalize("NFKD")
  .replace(/[̀-ͯ]/g, "").split(/[^a-z0-9#@]+/).filter(Boolean);

/**
 * A search box. matches(text) is true when every word typed starts a word
 * in the text ("road clos" finds "Road closed").
 */
export function useSearch() {
  const [query, setQuery] = useState("");
  const typed = useMemo(() => searchWords(query), [query]);
  const matches = useCallback((text) => {
    if (!typed.length) return true;
    const have = searchWords(text);
    return typed.every((w) => have.some((h) => h.startsWith(w)));
  }, [typed]);
  return { query, setQuery, searching: typed.length > 0, matches };
}

/** Is the person typing in a field? (Then shortcuts leave the key alone.) */
export const isTypingTarget = (el) => Boolean(el) && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));

/**
 * Listens for keys on the whole page while `active`. The handler isn't
 * called while someone is typing in a field, unless { inFields: true }
 * (for Escape closing a dialog, say).
 */
export function useKeyDown(handler, active = true, { inFields = false } = {}) {
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (event) => { if (inFields || !isTypingTarget(event.target)) latest.current(event); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, inFields]);
}

/**
 * State saved in this browser (localStorage) whenever it changes.
 * load(savedValue) turns what was saved (or null) into the starting value.
 * onSaveError is called if the browser's storage is full.
 */
export function useSavedState(key, load, onSaveError) {
  const [value, setValue] = useState(() => load(readSaved(key)));
  const errorRef = useRef(onSaveError);
  errorRef.current = onSaveError;
  useEffect(() => {
    const ok = writeSaved(key, value);
    if (errorRef.current) errorRef.current(!ok);
  }, [key, value]);
  return [value, setValue];
}

/**
 * Has the element come near the screen yet? For long lists (the Gallery):
 * each card draws when it's about to be seen, not all at once.
 *
 *   const [ref, seen] = useInView();
 *   <div ref={ref}>{seen && <ExpensiveDrawing />}</div>
 */
export function useInView(margin = "800px") {
  const ref = useRef(null);
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === "undefined");
  useEffect(() => {
    if (seen || !ref.current) return undefined;
    // Watch within the nearest scrolling panel, so "near" means near in that panel.
    let root = ref.current.parentElement;
    while (root && !/(auto|scroll)/.test(getComputedStyle(root).overflowY)) root = root.parentElement;
    const watch = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setSeen(true); watch.disconnect(); }
    }, { root: root || null, rootMargin: margin });
    watch.observe(ref.current);
    return () => watch.disconnect();
  }, [seen, margin]);
  return [ref, seen];
}

/**
 * Runs `fn` once, when the component first appears: never twice, even when
 * React StrictMode runs effects twice in development (issuing a passcode,
 * opening a shared design).
 */
export function useOnce(fn) {
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    fn();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * A value that has to load: `initial` until `load()` resolves, then its
 * result. Loads again when `deps` change (showing `initial` meanwhile); an
 * answer that arrives after that, or after the screen closed, is dropped.
 * A failed load leaves `initial`.
 *
 *   const src = useAsync(() => videoUrl(id), [id], null);
 */
export function useAsync(load, deps, initial = null) {
  const [value, setValue] = useState(initial);
  useEffect(() => {
    let alive = true;
    setValue(() => initial);
    Promise.resolve().then(load).then((v) => { if (alive) setValue(() => v); }, () => {});
    return () => { alive = false; };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return value;
}
